<template>
  <div class="home">
    <!-- 顶部导航固定定位 -->
    <header-nav class="fixed-header"></header-nav>

    <!-- 主内容区域，添加顶部内边距，避免被固定导航遮挡 -->
    <div class="main-content">
      <!-- 轮播广告 - 添加 advertCode 属性 -->
      <banner
        advertCode="NEW_HOME_BANNER"
        :pageSize="5"
        @load-success="handleBannerSuccess"
        @load-error="handleBannerError"
      ></banner>

      <!-- 分类导航 -->
      <category-nav-home @category-change="handleCategoryChange" />

      <!-- 新品上市 -->
      <new-products></new-products>

      <!-- 热卖和特价区域 -->
      <div class="sales-container">
        <hot-sales />
        <!-- 
          productInRow : 每行显示的商品数量 
          showProductName : 是否显示商品名称
          ProductPrice : 商品价格字段
        -->
        <special-price
          :pageSize="2"
          referenceCode="cc0a98a7c3894660aa132a5af0ede8cb"
          referenceKind="specialactivity"
          :productInRow="2"
          :showTitle="true"
          :showProductName="false"
          priceField="Param5"
        ></special-price>
      </div>

      <!-- 分类选择器 -->
      <CategorySelector
        ref="categorySelector"
        @category-change="handleCategoryChange"
        @update-images="handleImagesUpdate"
      />

      <!-- 分类主推内容 -->
      <CategoryFeature
        v-for="category in categories"
        :key="category.code"
        :advertCode="category.code"
        :categoryImages="categoryImages"
        @visibility-change="handleFeatureVisibility"
      />
    </div>

    <!-- 底部导航 -->
    <tab-bar></tab-bar>
  </div>
</template>

<script>
import HeaderNav from "@/components/HeaderNav.vue";
import Banner from "@/components/Banner.vue";
import CategoryNavHome from "@/components/CategoryNav-Home.vue";
import NewProducts from "@/components/NewProducts.vue";
import TabBar from "@/components/TabBar.vue";
import HotSales from "@/components/HotSales.vue";
import SpecialPrice from "@/components/SpecialPrice.vue";
import CategoryFeature from "@/components/CategoryFeature.vue";
import CategorySelector from "@/components/CategorySelector.vue";
import { CategoryFeatureCodes } from "@/api/constants/advertCodes";
import { ref, onUnmounted } from "vue";

export default {
  name: "Home",
  components: {
    HeaderNav,
    Banner,
    CategoryNavHome,
    NewProducts,
    TabBar,
    HotSales,
    SpecialPrice,
    CategoryFeature,
    CategorySelector,
  },
  beforeRouteEnter(to, from, next) {
    // 检查是否从 TabBar 进入（通过检查 from.name 是否为 TabBar 相关页面）
    const isFromTabBar = ["Home", "Category", "MyCenter"].includes(from.name);

    if (!isFromTabBar) {
      // --- 添加活动时间判断逻辑 ---
      // 定义活动开始和结束时间 (注意月份是从0开始的，所以5月是4)
      const promoStartDate = new Date(2025, 3, 30, 17, 15, 0); // 2025年5月1日 00:00:00
      const promoEndDate = new Date(2025, 4, 31, 23, 59, 59); // 2025年5月31日 23:59:59

      // 获取当前时间
      const now = new Date();

      // 判断当前时间是否在活动期间内
      const isPromoActive = now >= promoStartDate && now <= promoEndDate;
      console.log('[Home Guard] promoStartDate:', promoStartDate);
      console.log('[Home Guard] promoEndDate:', promoEndDate);
      console.log('[Home Guard] now:', now);
      console.log('[Home Guard] isPromoActive:', isPromoActive);

      // 只有在活动期间才重定向到 9th-anniversary 页面
      if (isPromoActive) {
        next({ name: "9thAnniversary" });
      } else {
        // 活动期间外，允许正常访问 Home 页面
        next();
      }
      // --- 活动时间判断逻辑结束 ---
    } else {
      // 如果是从 TabBar 进入，允许正常访问
      next();
    }
  },
  setup() {
    // 使用ref存储数据
    const categoryImages = ref({});
    const categories = ref([]);
    const dataReady = ref(false);
    const categorySelector = ref(null);
    const visibilityMap = ref(new Map());
    const isScrollLocked = ref(false); // 添加滚动锁定状态
    let visibilityUpdateTimeout;

    // 初始化分类列表
    const initCategories = () => {
      categories.value = Object.entries(CategoryFeatureCodes).map(
        ([key, code]) => ({
          code,
          key,
        })
      );
      // console.log('[Home] 初始化分类列表:', categories.value);
    };

    // 处理分类变化
    const handleCategoryChange = (category) => {
      isScrollLocked.value = true;

      const targetElement = document.querySelector(
        `[data-category="${category.url}"]`
      );

      if (targetElement) {
        const headerHeight = 50;
        const selectorHeight = 80;

        // 创建一个 Promise 来处理滚动完成事件
        const scrollPromise = new Promise((resolve) => {
          const targetPosition =
            targetElement.getBoundingClientRect().top + window.pageYOffset;
          const scrollPosition = targetPosition - headerHeight - selectorHeight;

          // 记录开始滚动的位置
          const startPosition = window.pageYOffset;
          const distance = Math.abs(scrollPosition - startPosition);

          // 根据滚动距离计算合适的动画时间，确保有足够时间完成滚动
          const scrollDuration = Math.min(Math.max(distance / 2, 500), 1000); // 最少500ms，最多1000ms

          window.scrollTo({
            top: scrollPosition,
            behavior: "smooth",
          });

          // 监听滚动结束
          let lastPosition = window.pageYOffset;
          const checkScrollEnd = setInterval(() => {
            const currentPosition = window.pageYOffset;
            if (currentPosition === lastPosition) {
              clearInterval(checkScrollEnd);
              resolve();
            }
            lastPosition = currentPosition;
          }, 50);

          // 设置超时保护，确保最终会解除锁定
          setTimeout(() => {
            clearInterval(checkScrollEnd);
            resolve();
          }, scrollDuration + 100);
        });

        // 等待滚动完成后再解除锁定
        scrollPromise.then(() => {
          // 添加一个小延迟，确保完全稳定
          setTimeout(() => {
            isScrollLocked.value = false;
          }, 100);
        });
      } else {
        console.warn("[Home] 未找到目标元素:", category.url);
        isScrollLocked.value = false;
      }
    };

    // 处理图片更新
    const handleImagesUpdate = (images) => {
      // console.log('[Home] 收到图片更新:', {
      //   receivedCount: Object.keys(images).length,
      //   codes: Object.keys(images)
      // });

      // 处理图片数据
      const processedImages = {};
      Object.entries(images).forEach(([advertCode, data]) => {
        // 确保advertCode和图片URL都存在
        if (advertCode && data.imageUrl) {
          processedImages[advertCode] = data.imageUrl;
          // console.log(`[Home] 处理分类图片:`, {
          //   advertCode,
          //   url: data.url,
          //   imageUrl: data.imageUrl,
          //   title: data.title
          // });
        } else {
          console.warn(`[Home] 跳过无效的图片数据:`, { advertCode, data });
        }
      });

      // 更新状态
      categoryImages.value = processedImages;

      // 初始化分类（如果还没有初始化）
      if (!dataReady.value) {
        dataReady.value = true;
        initCategories();
      }

      // 验证数据完整性
      // console.log('[Home] 数据更新完成:', {
      //   processedImagesCount: Object.keys(processedImages).length,
      //   categoriesCount: categories.value.length,
      //   imageKeys: Object.keys(processedImages),
      //   categoryKeys: categories.value.map(c => c.code)
      // });
    };

    // 处理 CategoryFeature 可见性变化
    const handleFeatureVisibility = (data) => {
      if (isScrollLocked.value) {
        return;
      }

      const { advertCode, ratio } = data;

      // 只在显著的可见性变化时更新
      const currentRatio = visibilityMap.value.get(advertCode) || 0;
      if (Math.abs(currentRatio - ratio) < 0.1) {
        return; // 忽略微小的变化
      }

      visibilityMap.value.set(advertCode, ratio);

      // 使用防抖处理可见性更新
      if (visibilityUpdateTimeout) {
        clearTimeout(visibilityUpdateTimeout);
      }

      visibilityUpdateTimeout = setTimeout(() => {
        let maxRatio = 0;
        let mostVisibleCode = null;

        visibilityMap.value.forEach((r, code) => {
          if (r > maxRatio) {
            maxRatio = r;
            mostVisibleCode = code;
          }
        });

        if (mostVisibleCode && maxRatio >= 0.5) {
          categorySelector.value?.updateActiveCategory(mostVisibleCode);
        }
      }, 150); // 添加150ms的防抖延迟
    };

    // 添加清理函数
    onUnmounted(() => {
      if (visibilityUpdateTimeout) {
        clearTimeout(visibilityUpdateTimeout);
      }
    });

    return {
      categories,
      categoryImages,
      handleCategoryChange,
      handleImagesUpdate,
      dataReady,
      categorySelector,
      handleFeatureVisibility,
      isScrollLocked,
    };
  },
  methods: {
    handleCategoryVisible(code) {
      // 通知CategorySelector更新选中状态
      this.$refs.categorySelector.updateActiveCategory(code);
    },
    handleBannerError(error) {
      console.error("Banner加载失败:", error);
      // 这里可以添加处理Banner加载错误的逻辑，比如显示错误提示
    },
    handleBannerSuccess() {
      console.log("Banner加载成功");
      // 这里可以添加Banner加载成功后的逻辑
    },
  },
};
</script>

<style lang="scss" scoped>
.home {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
}

.main-content {
  flex: 1;
  padding-top: var(--header-height, 44px);
  padding-bottom: var(--tabbar-height, 50px);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.sales-container {
  display: flex;
  padding: var(--container-padding, 2vw);
  gap: var(--container-gap, 2vw);
  box-sizing: border-box;
  width: 100%;

  @media screen and (max-width: 320px) {
    padding: var(--container-padding-small, 1vw);
    gap: var(--container-gap-small, 1vw);
  }

  @media screen and (min-width: 768px) {
    padding: var(--container-padding-large, 1.5vw);
    gap: var(--container-gap-large, 1.5vw);
    max-width: var(--max-content-width, 768px);
    margin: 0 auto;
  }
}

</style>
